import { Component, OnInit } from "@angular/core";
import { VTableRowStatus } from "@app/directives/_layout/v-table/_enums/VTableRowStatus.enum";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { IFilterBarTab, IVerticalFilterListItem } from "@app/directives/_resourceFilters/filter-bar/filter-bar.models";
import { VerticalFilterItemCreationCallback } from "@app/directives/_resourceFilters/vertical-filter-list/vertical-filter-list.component";
import { AccountFeatures } from "@app/enum/account/AccountFeature";
import { SortDirection } from "@app/enum/SortDirection";
import { SortType } from "@app/enum/SortType";
import { VehicleType } from "@app/enum/VehicleType.enum";
import { LatestSensorDataValues } from "@app/interfaces/ISensorDataValue";
import { IVehicle } from "@app/interfaces/IVehicle";
import { AccountFeatureService } from "@app/services/accountFeature.service";
import { ColourService } from "@app/services/colour.service";
import { TranslationService } from "@app/services/translation.service";
import { UnitConversionService, UnitConversionType } from "@app/services/unitConversion.service";
import { VehicleService } from "@app/services/vehicle.service";
import { EBPMSStatusIconComponent } from "../ebpms/ebpms-status-icon/ebpms-status-icon.component";
import { EBPMSService } from "../ebpms/ebpms.service";
import { EBPMSStatus } from "../ebpms/_models/EBPMSStatus";
import { FaultCodesService } from "../faultCodes/fault-codes.service";
import { IDiagnosticRecord } from "../faultCodes/_models/IDiagnosticRecord";
import { IUserFriendlySensorData, SensorDataService } from "../sensorData/sensor-data.service";
import './maintenance-overview.component.scss';
import { IMaintenanceRowData } from "./_models/IMaintenanceRowData";
import { IBrakePerformanceData } from "../ebpms/_models/IBrakePerformanceData";
import { AccountService } from "@app/services/account.service";

@Component({
    selector: 'maintenance-overview',
    template: require('./maintenance-overview.component.html')
})
export class MaintenanceOverviewComponent implements OnInit {
    tableColumns: IVTableColumn<IMaintenanceRowData>[] = [];
    tableRows: IVTableRow<IMaintenanceRowData>[] = [];
    filterTabs: IFilterBarTab<IMaintenanceRowData>[] = [];

    selectedRowData: IMaintenanceRowData;
    selectedVehicleUserFriendlySensorData: IUserFriendlySensorData[];
    selectedVehicleIcon: string;
    selectedVehicleType: string;

    loading: boolean;

    private vehicles: IVehicle[];
    private diagnosticRecords: IDiagnosticRecord[];
    private maintenanceRowData: IMaintenanceRowData[];
    private sensorData: LatestSensorDataValues[];
    private brakePerformanceData: IBrakePerformanceData[];

    private ebpmsEnabled = false;

    constructor(
        private readonly vTableService: VTableService,
        private readonly vehicleService: VehicleService,
        private readonly faultCodesService: FaultCodesService,
        private readonly sensorDataService: SensorDataService,
        private readonly unitConversionService: UnitConversionService,
        private readonly translationService: TranslationService,
        private readonly ebpmsService: EBPMSService,
        private readonly colourService: ColourService,
        private readonly accountFeatureService: AccountFeatureService,
        private readonly accountService: AccountService) {
    }

    async ngOnInit(): Promise<void> {

        const accountData = this.accountService.getAccountDataFromCache();        
        this.ebpmsEnabled = this.accountFeatureService.accountHasAnyFeatureAccess(accountData, [AccountFeatures.Ebpms]);        
        
        this.createTabs();
        this.createTableColumns();

        await this.loadData();
    }

    async vehicleSelected(row: IVTableRow<IMaintenanceRowData>): Promise<void> {

        this.selectedRowData = row.data;
        this.selectedVehicleUserFriendlySensorData = null;
        this.selectedVehicleIcon = this.vehicleService.getIconForVehicleType(row.data.Vehicle?.VehicleType);
        this.selectedVehicleType = this.translationService.tEnum(VehicleType, "VehicleType", row.data.Vehicle.VehicleType);

        this.getSelectedVehicleData();
    }

    sidebarClosed(): void {
        this.selectedRowData = null;
    }

    ebpmsCreationCallback: VerticalFilterItemCreationCallback<IMaintenanceRowData> = (maintenanceRow) => {

        if (maintenanceRow.BrakePerformanceData?.Status == null) {
            return null;
        }

        return {
            name: EBPMSStatus[maintenanceRow.BrakePerformanceData?.Status],
            icon: this.ebpmsService.getStatusIcon(maintenanceRow.BrakePerformanceData?.Status),
            data: maintenanceRow,
            color: this.getEBPMSColour(maintenanceRow.BrakePerformanceData?.Status),
            predicate: (r) => r.BrakePerformanceData?.Status === maintenanceRow.BrakePerformanceData?.Status
        };
    }

    faultCodeCreationCallback: VerticalFilterItemCreationCallback<IMaintenanceRowData> = (maintenanceRow) => {

        if (maintenanceRow.Dtcs?.length == 0) {
            return null;
        }

        var dtcs: IVerticalFilterListItem<IMaintenanceRowData>[] = [];

        maintenanceRow.Dtcs.forEach(dtc => {

            dtcs.push({
                name: dtc.FriendlyCode,
                data: maintenanceRow,
                predicate: (r) => r.Dtcs.some(d => d?.DiagnosticTroubleCode === dtc.DiagnosticTroubleCode)
            });
        })

        return dtcs;
    }

    // ---

    private async loadData(): Promise<void> {
        this.loading = true;

        const [vehicles, diagnosticRecords, sensorData, brakePerformanceData] = await Promise.all([
            this.vehicleService.getVehicles({ includeLatestPlots: true, includeVOR: true }) as Promise<IVehicle[]>,
            this.faultCodesService.getActiveDiagnosticRecords() as Promise<IDiagnosticRecord[]>,
            this.sensorDataService.getLatestSensorDataValues() as Promise<LatestSensorDataValues[]>,
            this.ebpmsService.getLatestBrakePerformanceDataForAccount() as Promise<IBrakePerformanceData[]>,
        ]);

        this.vehicles = vehicles;
        this.diagnosticRecords = diagnosticRecords;
        this.sensorData = sensorData;
        this.brakePerformanceData = brakePerformanceData;

        this.diagnosticRecords.forEach(r => r.FriendlyCode = this.faultCodesService.reformatSPNCode(r.FriendlyCode));

        this.maintenanceRowData = this.vehicles
            .map(v => {

                var brakePerformanceResponse = this.brakePerformanceData.find(s => s.VehicleId === v.Id);                

                if (brakePerformanceResponse != undefined && this.ebpmsService.isLatestEBPMSHistoryOutOfDate(brakePerformanceResponse)) {
                    brakePerformanceResponse.Status = EBPMSStatus.Unknown;
                }

                const rowData: IMaintenanceRowData = {
                    Id: v.Id,
                    Vehicle: v,
                    Dtcs: this.diagnosticRecords.filter(d => d.VehicleId === v.Id) || null,
                    SensorData: this.sensorData.find(s => s.vehicleId === v.Id) || null,
                    BrakePerformanceData: brakePerformanceResponse
                };

                rowData.RowStatus = this.createRowStatus(rowData);
                return rowData;
            });

        this.tableRows = this.vTableService.createTableRows(this.maintenanceRowData, this.tableColumns);

        this.loading = false;
    }

    private createTableColumns(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createStatusTableHeader({ sorted: true, sortDirection: SortDirection.Descending }),
                createCellFunction: (data) => this.vTableService.createStatusCell(data.RowStatus)
            },
            {
                header: this.vTableService.createVehicleTableHeader(),
                createCellFunction: (data) => this.vTableService.createVehicleRegCell(data.Vehicle)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "MaintenanceOverview.TableColumnHeaderVehicleType", width: 140 }),
                createCellFunction: (data) => this.vTableService.createTableCell(this.translationService.tEnum(VehicleType, "VehicleType", data.Vehicle.VehicleType))
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "MaintenanceOverview.TableColumnHeaderEBPMSStatus", sortType: SortType.String, width: 120 }),
                createCellFunction: (data) => this.vTableService.createTableCell(data.BrakePerformanceData?.Status, { component: EBPMSStatusIconComponent, componentInputs: { ebpmsStatus: data.BrakePerformanceData?.Status } })
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "MaintenanceOverview.TableColumnHeaderEBPMSValue", sortType: SortType.Number, width: 100 }),
                createCellFunction: (data) => this.vTableService.createTableCell(data.BrakePerformanceData?.Score.toFixed(2).toString())
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "MaintenanceOverview.TableColumnHeaderVOR", sortType: SortType.String, width: 100 }),
                createCellFunction: (data) => this.vTableService.createTableCell(data.Vehicle.vorRecord ? "VOR" : null, data.Vehicle.vorRecord ? { classes: "vor" } : {} )
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "FaultCodes.ActiveFaultCodes", sortType: SortType.String }),
                createCellFunction: (data) => this.vTableService.createTableCell(this.faultCodesService.getFriendlyCodesString(data.Dtcs))
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "MaintenanceOverview.TableColumnHeaderAuxEngineOnHours", width: 150, minTableWidth: 1050 }),
                createCellFunction: (data) => {
                    var engineHours = this.sensorDataService.getEngineHours(data.SensorData);
                    return this.vTableService.createTableCell(engineHours?.toLocaleString(), { data: engineHours })
                }
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "MaintenanceOverview.TableColumnHeaderOdometer", width: 120, minTableWidth: 900 }),
                createCellFunction: (data) => {
                    var odoNumber = this.unitConversionService.convertUnitWithFormatting(data.Vehicle.Plot?.Odometer / 1000, UnitConversionType.Distance, 0);
                    var odoWithSuffix = this.unitConversionService.convertUnitWithSuffix(data.Vehicle.Plot?.Odometer / 1000, UnitConversionType.Distance, 0);
                    return this.vTableService.createTableCell(data.Vehicle.Plot?.Odometer ? odoWithSuffix : null, { sortData: data.Vehicle.Plot?.Odometer, exportData: odoNumber })
                }
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: "MaintenanceOverview.TableColumnHeaderLastGps", sortable: true, sortDirection: SortDirection.Descending, sortType: SortType.Date }),
                createCellFunction: (data) => this.vTableService.createDateCell(data.Vehicle?.Plot?.Utc)
            }
        ]

        // Remove EBPMS columns if required
        if (!this.ebpmsEnabled) {
            this.tableColumns = this.tableColumns.filter(c => c.header.title.indexOf("EBPMS") < 0 && c.header.title.indexOf("BPV") < 0);
        }
    }

    // --

    private createRowStatus(data: IMaintenanceRowData): VTableRowStatus {
        if (data.Vehicle.vorRecord) {
            return VTableRowStatus.Info;
        }

        switch (data.BrakePerformanceData?.Status) {
            case EBPMSStatus.Critical:
                return VTableRowStatus.Error;

            case EBPMSStatus.Warning:
            case EBPMSStatus.Unknown:
                return VTableRowStatus.Warning;
        }

        if (data.Dtcs?.length > 0) {
            return VTableRowStatus.Warning;
        }

        return VTableRowStatus.Success;
    }

    private createTabs(): void {
        this.filterTabs = [];

        // Todo: Show hide based on account features
        this.createTabDefinition(this.translationService.t("Common.All"), (r) => true);
        this.createTabDefinition(this.translationService.t("MaintenanceOverview.TabTitleNoIssues"), v => v.RowStatus == VTableRowStatus.Success);
        if (this.ebpmsEnabled) {
            this.createTabDefinition(this.translationService.t("MaintenanceOverview.TabTitleEbpmsWarnings"), v => v.BrakePerformanceData?.Status != null && v.BrakePerformanceData?.Status != EBPMSStatus.Good);
        }
        this.createTabDefinition(this.translationService.t("MaintenanceOverview.TabTitleFaultCodes"), v => v.Dtcs?.length > 0);
        this.createTabDefinition(this.translationService.t("MaintenanceOverview.TabTitleVor"), v => !!v.Vehicle.vorRecord);
    }

    private createTabDefinition(translationString: string, callback: (report: IMaintenanceRowData) => boolean) {
        this.filterTabs.push({ title: translationString, predicate: callback });
    }

    private async getSelectedVehicleData(): Promise<void> {
        if (this.selectedRowData.SensorData) {
            this.selectedVehicleUserFriendlySensorData = this.sensorDataService.getUserFriendlySensorDataValues(this.selectedRowData.SensorData);
        }
    }

    private getEBPMSColour(status: EBPMSStatus): string {
        switch (status) {
            case EBPMSStatus.Good:
                return this.colourService.greenColor;

            case EBPMSStatus.Warning:
            case EBPMSStatus.Unknown:
                return this.colourService.orangeColor;

            case EBPMSStatus.Critical:
                return this.colourService.redColor;

            default:
                return this.colourService.greyColor;
        }
    }
}
